/**
 * useMLData - Shared Data Fetching Hook for All ML Infrastructure Pages
 * 
 * Provides consistent data fetching with loading, error states, and refresh.
 * 
 * Following BRAIN-PRE-CODE-CHECKLIST.md:
 * - Clean single imports
 * - Defensive null checks
 * - Proper error handling
 * - No silent failures
 */

import { useState, useEffect, useCallback, useRef } from 'react';

interface UseMLDataOptions {
  autoFetch?: boolean;
  pollingInterval?: number;
}

interface UseMLDataReturn<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  setData: (data: T | null) => void;
}

export function useMLData<T>(
  fetchFn: () => Promise<T>,
  options: UseMLDataOptions = {}
): UseMLDataReturn<T> {
  const { autoFetch = true, pollingInterval } = options;
  
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(autoFetch);
  const [error, setError] = useState<string | null>(null);
  
  // Store fetchFn in ref to avoid dependency issues with inline arrow functions
  // This prevents infinite loops when callers pass () => apiClient.method()
  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;
  
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  
  // Track if initial fetch has been done to prevent duplicate fetches
  const hasFetchedRef = useRef(false);

  const fetchData = useCallback(async (isPolling = false) => {
    try {
      // Only show loading state on initial fetch, not during polling refreshes
      // This prevents flickering every 30 seconds
      if (!isPolling) {
        setLoading(true);
        setError(null);
      }
      const result = await fetchFnRef.current();
      if (isMountedRef.current) {
        setData(result);
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data';
        setError(errorMessage);
        console.error('useMLData fetch error:', err);
      }
    } finally {
      // Only update loading state if this wasn't a polling refresh
      if (isMountedRef.current && !isPolling) {
        setLoading(false);
      }
    }
  }, []); // No dependencies - uses refs

  const refresh = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  // Initial fetch - only once
  useEffect(() => {
    if (autoFetch && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchData();
    }
  }, [autoFetch, fetchData]);
  
  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Polling - with stable interval
  useEffect(() => {
    if (!pollingInterval) return;

    const intervalId = setInterval(() => {
      if (isMountedRef.current) {
        fetchData(true); // Pass true to indicate this is a polling refresh
      }
    }, pollingInterval);

    return () => clearInterval(intervalId);
  }, [pollingInterval, fetchData]);

  return {
    data,
    loading,
    error,
    refresh,
    setData
  };
}

export default useMLData;
